import axios from "axios";
import { reactive, ref } from "vue";
import {
  getDateFromMonthYear,
  getDatesBetween,
  getWeekNumber,
} from "common/dateHelpers";
import { getCurrentSchoolYear } from "composables/useHelpers";
import mitt from "mitt";
import { cloneDeep } from "lodash";

const toPixels = (value) => `${value}px`;

const TIMELINE_COLUMN_WIDTH = 40;
export const TIMELINE_COLUMN_WIDTH_IN_PIXELS = toPixels(TIMELINE_COLUMN_WIDTH);

export const TIMELINE_CALENDAR_TYPES = ["schoolYear", "period"];
export const TIMELINE_CALENDAR_DEFAULT_TYPE = TIMELINE_CALENDAR_TYPES[0];

const DEFAULT_FETCHED_DATA_SCHOOLYEAR = {
  days: [],
  weeks: [],
  months: [],
  periods: [],
};

const DEFAULT_GRID_DATA = {
  days: [],
  weeks: [],
  months: [],
  periods: [],
};

const DEFAULT_TIMELINE_CALENDAR = {
  fetchedData: {},
  grid: DEFAULT_GRID_DATA,
  calendarType: TIMELINE_CALENDAR_DEFAULT_TYPE,
  view: {
    showDays: true,
    showWeeks: true,
    showMonths: true,
    showPeriods: true,
    showCurrentDayIndicator: true,
  },
};

export class TimelineCalendarModel {
  constructor(data = {}, learningObjectivesStore = null) {
    if (!learningObjectivesStore) {
      throw new Error("Learning objectives store is required");
    }

    // Get the default timeline calendar options
    const { view = {}, calendarType = null, period = null } = data;

    // This will hold all the fetched data like days, weeks, months, etc.
    // { '2024-2025': { weeks: [], months: [], days: [], periods: [] }}
    this.fetchedData = ref({});
    this.grid = ref(structuredClone(DEFAULT_TIMELINE_CALENDAR.grid));
    this.view = reactive(
      structuredClone(Object.assign({}, DEFAULT_TIMELINE_CALENDAR.view, view)),
    );

    // Model state
    this.isInitialized = ref(false);
    this.loading = ref(false);

    // Model period
    this.selectedPeriodIndex = ref(null);
    this.hasPreviousPeriod = ref(false);
    this.hasNextPeriod = ref(false);

    // Model school year
    this.selectedSchoolYearIndex = ref(null);
    this.hasPreviousSchoolYear = ref(false);
    this.hasNextSchoolYear = ref(false);

    // Private properties, these will go through a setter and getter
    this._selectedSchoolYear = ref(null);
    this._selectedPeriod = ref(null);
    this._calendarType = ref(
      calendarType ?? DEFAULT_TIMELINE_CALENDAR.calendarType,
    );

    // Define learningObjectivesStore as a non-enumerable property
    Object.defineProperty(this, "_learningObjectivesStore", {
      value: learningObjectivesStore,
      writable: false,
      configurable: false,
      enumerable: false,
    });

    // Define the emitter as a non-enumerable property
    Object.defineProperty(this, "_emitter", {
      value: mitt(),
      writable: false,
      configurable: false,
      enumerable: false,
    });

    this.init({ period });
  }

  async init({ period = null } = {}) {
    // Fetch the weeks of the current school year
    // We should fix the replace function and find a better solid solution
    await Promise.all([
      this._learningObjectivesStore.fetchSchoolYears(),
      this.fetchTimelineDates(getCurrentSchoolYear().replace("/", "-")),
    ]);

    this.setInitialSchoolYear();

    await this.fetchPeriods();

    if (period !== null) {
      this.selectedPeriod = period;
    }

    // Generate a new grid based on the selected calendar type
    this.generateGrid();

    this.isInitialized.value = true;
  }

  setInitialSchoolYear() {
    const foundSchoolYear = this.schoolYears.find(
      ({ label }) => label === getCurrentSchoolYear().replace("/", "-"),
    );

    if (!foundSchoolYear) {
      throw new Error(
        `School year ${getCurrentSchoolYear().replace("/", "-")} not found`,
      );
    }

    this.selectedSchoolYear = foundSchoolYear;
  }

  get schoolYears() {
    return this._learningObjectivesStore.schoolYears;
  }

  get gridCount() {
    return this.showDays
      ? this.grid.value.days.length
      : this.grid.value.weeks.length;
  }

  get showDays() {
    return this.view.showDays;
  }

  get selectedSchoolYear() {
    return this._selectedSchoolYear;
  }

  set selectedSchoolYear(value) {
    if (value === this._selectedSchoolYear.value) {
      return;
    }

    this._selectedSchoolYear.value = value;

    const schoolYears = this.schoolYears;
    const currentSchoolYearIndex = schoolYears.findIndex(
      ({ label }) => label === value.label,
    );

    if (currentSchoolYearIndex === -1) {
      throw new Error(
        `Period ${this._selectedSchoolYear.value.label} not found in fetched data`,
      );
    }

    this.selectedSchoolYearIndex.value = currentSchoolYearIndex;
    this.hasPreviousSchoolYear.value = currentSchoolYearIndex > 0;
    this.hasNextSchoolYear.value =
      currentSchoolYearIndex < schoolYears.length - 1;
  }

  get selectedPeriod() {
    return this._selectedPeriod;
  }

  set selectedPeriod(value) {
    if (value === this._selectedPeriod.value) {
      return;
    }

    this._selectedPeriod.value = value;

    const periods =
      this.fetchedData.value[this.selectedSchoolYear.value.label].periods;

    if (!periods.length) {
      // There are no periods in this school year, so we set these options to false
      this.selectedPeriodIndex.value = 0;
      this.hasPreviousPeriod.value = false;
      this.hasNextPeriod.value = false;

      this._emitter.emit("selectedPeriodChanged", value);

      return;
    }

    const currentPeriodIndex = periods.findIndex(({ id }) => id === value.id);

    if (currentPeriodIndex === -1) {
      throw new Error(
        `Period ${this.selectedPeriod.value.id} not found in fetched data`,
      );
    }

    this.selectedPeriodIndex.value = currentPeriodIndex;
    this.hasPreviousPeriod.value = currentPeriodIndex > 0;
    this.hasNextPeriod.value = currentPeriodIndex < periods.length - 1;

    this._emitter.emit("selectedPeriodChanged", value);
  }

  onSelectedPeriodChange(cb) {
    this._emitter.on("selectedPeriodChanged", cb);
  }

  offSelectedPeriodChange(cb) {
    this._emitter.off("selectedPeriodChanged", cb);
  }

  async changeSelectedSchoolYear(direction = null) {
    // Expects direction to be either 'previous' or 'next'
    if (direction === null || !["previous", "next"].includes(direction)) {
      throw new Error(
        "Direction is required and should be either 'previous' or 'next'",
      );
    }

    switch (direction) {
      case "previous":
        if (!this.hasPreviousSchoolYear.value) {
          console.log("No previous school year");
          return;
        }

        const previousSchoolYear =
          this.schoolYears[this.selectedSchoolYearIndex.value - 1];

        // This will fetch the weeks of the previous school year
        await this.fetchTimelineDates(previousSchoolYear.label);

        // Set the previous school year as the current selected school year
        this.selectedSchoolYear = previousSchoolYear;

        // This will fetch the periods of the previous school year
        await this.fetchPeriods({
          forceFetch: true,
          schoolYear: previousSchoolYear.label,
        });

        // Generate a new grid based on the selected calendar type
        this.generateGrid();

        break;
      case "next":
        if (!this.hasNextSchoolYear.value) {
          console.log("No next school year");
          return;
        }

        const nextSchoolYear =
          this.schoolYears[this.selectedSchoolYearIndex.value + 1];

        // This will fetch the weeks of the next school year
        await this.fetchTimelineDates(nextSchoolYear.label);

        // Set the next school year as the current selected school year
        this.selectedSchoolYear = nextSchoolYear;

        // This will fetch the periods of the previous school year
        await this.fetchPeriods({
          forceFetch: true,
          schoolYear: nextSchoolYear.label,
        });

        // Generate a new grid based on the selected calendar type
        this.generateGrid();

        break;
    }
  }

  changeSelectedPeriod(direction = null) {
    // Expects direction to be either 'previous' or 'next'
    if (direction === null || !["previous", "next"].includes(direction)) {
      throw new Error(
        "Direction is required and should be either 'previous' or 'next'",
      );
    }

    const periods =
      this.fetchedData.value[this.selectedSchoolYear.value.label].periods;

    switch (direction) {
      case "previous":
        if (!this.hasPreviousPeriod.value) {
          console.log("No previous period");
          return;
        }

        this.selectedPeriod = periods[this.selectedPeriodIndex.value - 1];

        // Generate a new grid based on the selected calendar type
        this.generateGrid();

        return this.selectedPeriod.value;
      case "next":
        if (!this.hasNextPeriod.value) {
          console.log("No next period");
          return;
        }

        this.selectedPeriod = periods[this.selectedPeriodIndex.value + 1];

        // Generate a new grid based on the selected calendar type
        this.generateGrid();

        return this.selectedPeriod.value;
    }
  }

  get calendarType() {
    return this._calendarType;
  }

  set calendarType(value) {
    if (!TIMELINE_CALENDAR_TYPES.includes(value)) {
      throw new Error(
        `Invalid calendar type. Expected one of ${TIMELINE_CALENDAR_TYPES.join(
          ", ",
        )}, but received ${value}`,
      );
    }

    if (value === this._calendarType.value) {
      return;
    }

    this._calendarType.value = value;

    // Generate a new grid based on the selected calendar type
    this.generateGrid();
  }

  updateView({ showDays, showWeeks, showMonths, showPeriods, calendarType }) {
    this.view.showDays = showDays;
    this.view.showWeeks = showWeeks;
    this.view.showMonths = showMonths;
    this.view.showPeriods = showPeriods;

    this.calendarType = calendarType;
  }

  async fetchTimelineDates(schoolYear = null) {
    // schoolYear should be in the format YYYY-YYYY
    if (!schoolYear) {
      throw new Error("School year is required");
    }

    if (this.fetchedData.value?.[schoolYear]) {
      return this.fetchedData.value?.[schoolYear];
    }

    try {
      const {
        data: { data: fetchedWeeks },
      } = await axios.get(`api/school-year-weeks/${schoolYear}`);

      this.setFetchedDataBySchoolyear(schoolYear, fetchedWeeks);

      return this.fetchedData.value[schoolYear];
    } catch (error) {
      console.log("🚀 ~ fetchTimelineDates ~ error:", error);
    }
  }

  setFetchedDataBySchoolyear(schoolYear = null, weeks = []) {
    if (!schoolYear) {
      throw new Error("School year is required");
    }

    // DEFAULT_FETCHED_DATA_SCHOOLYEAR = { days: [], weeks: [], months: [], periods: [] }
    this.fetchedData.value[schoolYear] = structuredClone(
      DEFAULT_FETCHED_DATA_SCHOOLYEAR,
    );

    const model = this.fetchedData.value[schoolYear];

    weeks.forEach((week) => {
      const { start, end } = week;

      // This will hold an array of date objects from the start to the end date
      const daysInCurrentWeek = getDatesBetween(start, end);

      // Push every week to the weeks array
      model.weeks.push({
        timestamp: new Date(start).setHours(0, 0, 0, 0),
        days: daysInCurrentWeek.length,
        ...week,
      });

      // Push every day of the current week to the days array
      daysInCurrentWeek.forEach((date) => {
        model.days.push(date.getTime());
      });

      this.setMonthsByWeek(model.months, week);
    });
  }

  setMonthsByWeek(model = null, week = null) {
    if (!model || !week) {
      throw new Error("Model and week are required");
    }

    const { start, end, month, year } = week;

    // This will hold an array of date objects from the start to the end date
    const daysInCurrentWeek = getDatesBetween(start, end);

    // Get the name of the month from the current week
    const currentMonthFromWeek = getDateFromMonthYear(month, year);

    // Because we loop through the weeks, we need to check if the month already exists
    // If it doesn't exist, we create it
    // If it exists, we need to update the number of weeks and days
    let foundMonth = model.find(({ name }) => name === currentMonthFromWeek);

    if (!foundMonth) {
      // Create a new month object and push it to the months array
      foundMonth = {
        name: currentMonthFromWeek,
        weeks: 0,
        days: 0,
      };

      model.push(foundMonth);
    }

    // Update the number of weeks and days in the month
    foundMonth.days += daysInCurrentWeek.length;
    foundMonth.weeks++;

    return foundMonth;
  }

  async fetchPeriods({
    forceFetch = false,
    schoolYear = getCurrentSchoolYear().replace("/", "-"),
  } = {}) {
    // schoolYear should be in the format YYYY-YYYY
    if (!schoolYear) {
      throw new Error("School year is required");
    }

    if (this.fetchedData?.[schoolYear]?.periods) {
      this.setCurrentPeriod(schoolYear);

      return this.fetchedData[schoolYear].periods;
    }

    const fetchedPeriods = await this._learningObjectivesStore.fetchPeriods({
      forceFetch,
      schoolYear,
    });

    // This will create a new object with the school year as the key and an array of the periods as the value
    // We have to use the replace function to convert the school year from YYYY/YYYY to YYYY-YYYY
    const groupedPeriod = Object.groupBy(fetchedPeriods, ({ school_year }) =>
      school_year.replace("/", "-"),
    );

    if (groupedPeriod[schoolYear] === undefined) {
      this.selectedPeriod = null;

      return;
    }

    const model = this.fetchedData.value[schoolYear];

    // Set the periods to the model
    model.periods = groupedPeriod[schoolYear].map((period) => {
      // Find the start index of the period in the weeks array
      const startWeekIndex = model.weeks.findIndex(
        ({ week, year }) =>
          week === period.start_week && year === period.start_year,
      );

      // Find the end index of the period in the weeks array
      const endWeekIndex = model.weeks.findIndex(
        ({ week, year }) =>
          week === period.end_week && year === period.end_year,
      );

      // If the start or end index is -1, it means the period is invalid
      if (startWeekIndex === -1 || endWeekIndex === -1) {
        throw new Error("Invalid period data");
      }

      // Calculate the number of days in the period
      let days = 0;

      // Loop through the weeks in the period and sum the days
      for (let i = startWeekIndex; i <= endWeekIndex; i++) {
        days += model.weeks[i].days;
      }

      // Set the amount of days in the period
      period.days = days;

      // Set the amount of weeks in the period
      period.weeks = endWeekIndex - startWeekIndex + 1;

      return period;
    });

    this.setCurrentPeriod(schoolYear);
  }

  setCurrentPeriod(schoolYear = null) {
    // schoolYear should be in the format YYYY-YYYY
    if (!schoolYear) {
      throw new Error("School year is required");
    }

    const model = this.fetchedData.value[schoolYear];

    // We have to set the selected period to the first period in the array
    const foundCurrentPeriod = model.periods.find(
      ({ is_current }) => is_current,
    );

    if (foundCurrentPeriod) {
      this.selectedPeriod = foundCurrentPeriod;
    } else {
      this.selectedPeriod = model.periods[0];
    }
  }

  setCurrentDayInGrid() {
    const [currentWeek = null, currentYear = null] = getWeekNumber() || [];

    if (!currentWeek || !currentYear) {
      throw new Error(
        "Could not get the current week or year. Please check your date helpers.",
      );
    }

    const { weeks = [] } = this.grid.value || {};

    const foundCurrentWeek = weeks.find(
      ({ week, year }) => week === currentWeek && year === currentYear,
    );

    if (!foundCurrentWeek) {
      // If we don't find the current week in the grid, we can't set the indicator
      return;
    }

    foundCurrentWeek.isCurrentWeek = true;
  }

  generateGrid() {
    // This function might take a while to run, so we need to show a loading spinner
    this.loading.value = true;

    // Reset the current grid
    this.grid.value = structuredClone(DEFAULT_TIMELINE_CALENDAR.grid);

    switch (this.calendarType.value) {
      case "period":
        this.generateGridBySelectedPeriod();
        break;
      case "schoolYear":
        this.generateGridBySelectedSchoolYear();
        break;
    }

    this.setCurrentDayInGrid();

    this.loading.value = false;
  }

  generateGridBySelectedPeriod() {
    // Check if the selected period is valid
    if (
      this.selectedPeriod.value === null ||
      !this.fetchedData.value[this.selectedSchoolYear.value.label].periods
        .length
    ) {
      // If there are no periods in this school year, we need to generate the grid by the selected school year
      this.generateGridBySelectedSchoolYear();

      return;
    }

    if (!this.selectedSchoolYear.value.label) {
      throw new Error("School year is required");
    }

    const {
      start_week: currentPeriodStartWeek,
      start_year: currentPeriodStartYear,
      weeks: currentPeriodDurationInWeeks,
    } = this.selectedPeriod.value;

    // Data to build the grid from
    const gridData =
      this.fetchedData.value[this.selectedSchoolYear.value.label];

    // Find the starting index of the period in the weeks array
    const startWeekIndex = gridData.weeks.findIndex(
      ({ week, year }) =>
        currentPeriodStartWeek === week && currentPeriodStartYear === year,
    );

    if (startWeekIndex === -1) {
      throw new Error(
        `Period ${currentPeriodStartWeek}-${currentPeriodStartYear} not found in grid data`,
      );
    }

    const totalWeeksInPeriod = gridData.weeks.slice(
      startWeekIndex,
      startWeekIndex + currentPeriodDurationInWeeks,
    );

    // Set the weeks in the grid
    this.grid.value.weeks = totalWeeksInPeriod;

    // Find the starting index of the first day by the first week
    const startWeekTimestamp = totalWeeksInPeriod[0].timestamp;

    // Find the starting index of the days in the current grid data
    const startDayIndex = gridData.days.findIndex(
      (dayTimestamp) => dayTimestamp === startWeekTimestamp,
    );

    if (startDayIndex === -1) {
      throw new Error(`Start day ${startWeekTimestamp} not found in grid data`);
    }

    // Calculate the total number of days in the current period
    const totalDaysInCurrentPeriod = totalWeeksInPeriod.reduce(
      (acc, { days }) => acc + days,
      0,
    );

    // Set the days in the grid
    this.grid.value.days = gridData.days.slice(
      startDayIndex,
      startDayIndex + totalDaysInCurrentPeriod,
    );

    // Set the months in the grid
    totalWeeksInPeriod.forEach((week) => {
      this.setMonthsByWeek(this.grid.value.months, week);
    });

    // Set the periods in the grid
    this.grid.value.periods.push(this.selectedPeriod.value);
  }

  generateGridBySelectedSchoolYear() {
    // Check if the selected school year is valid
    if (!this.selectedSchoolYear.value.label) {
      throw new Error("School year is required");
    }

    // Data to build the grid from
    const gridData =
      this.fetchedData.value[this.selectedSchoolYear.value.label];

    this.grid.value.days = gridData.days;
    this.grid.value.weeks = gridData.weeks;

    // Set the months in the grid
    gridData.weeks.forEach((week) => {
      this.setMonthsByWeek(this.grid.value.months, week);
    });

    // Set the periods by first setting the weeks and then slice in the periods
    this.grid.value.periods = cloneDeep(gridData.weeks).map((week) => {
      week.type = "empty";

      return week;
    });

    gridData.periods.forEach((period) => {
      // Find the starting index of the period in the grid
      const foundPeriodStartingIndex = this.grid.value.periods.findIndex(
        ({ week, year }) =>
          week === period.start_week && year === period.start_year,
      );

      if (!foundPeriodStartingIndex) {
        throw new Error("Invalid period data", period.name);
      }

      this.grid.value.periods.splice(
        foundPeriodStartingIndex,
        period.weeks,
        period,
      );
    });
  }
}

export default TimelineCalendarModel;
